import { useLocale as useLocaleR42 } from '@/lib/locale';
import React from 'react';
import { MapPin, Award } from 'lucide-react';

/**
 * CoachIntroHero
 * --------------
 * The "who you're applying to" hero rendered on the entry step of the
 * parent registration wizard. Powers the friendly link that Starter-plan
 * coaches share — without a paid landing page they previously sent
 * parents straight into a generic form; now their photo, name, bio, and
 * credentials anchor the experience so the parent immediately knows the
 * human behind the swim school.
 *
 * Only rendered when we have at least one piece of coach context to show
 * (display_name is the minimum; everything else degrades gracefully).
 *
 * Reads exclusively from the `/public/coach/{slug}` payload — no backend
 * changes needed for Phase 44.3.
 */
export function CoachIntroHero({ coach }) {
    const { t: t42 } = useLocaleR42();
    if (!coach || !coach.display_name) return null;

    const accent = coach.primary_color || '#00d4ff';
    const photoUrl = coach.photo_url || '';
    const initials = (coach.display_name || 'C')
        .split(/\s+/)
        .map((s) => s[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    const bio = (coach.bio || '').trim();
    const certs = Array.isArray(coach.certifications) ? coach.certifications.filter(Boolean) : [];
    const locationLabel = coach.pool_name || coach.service_area || '';
    const yearsLabel = coach.years_experience ? `${coach.years_experience}+ year${coach.years_experience === 1 ? '' : 's'} coaching` : '';

    return (
        <div
            className="overflow-hidden rounded-2xl border border-[#0a1628]/8 bg-white shadow-[0_10px_30px_rgba(10,22,40,0.08)]"
            data-testid="coach-intro-hero"
        >
            {/* Branded color band — uses the coach's primary_color so the
                hero feels personal even before they look at the photo. */}
            <div
                className="h-3 w-full"
                style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}cc 100%)` }}
                aria-hidden="true"
            />
            <div className="p-5 sm:p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
                    {/* Photo (or initials fallback) */}
                    <div
                        className="relative h-24 w-24 shrink-0 overflow-hidden rounded-2xl ring-4 ring-white sm:h-28 sm:w-28"
                        style={{ boxShadow: `0 0 0 3px ${accent}33` }}
                        data-testid="coach-intro-photo"
                    >
                        {photoUrl ? (
                            <img
                                src={photoUrl}
                                alt={`${coach.display_name}, your swim coach`}
                                className="h-full w-full object-cover"
                                loading="eager"
                            />
                        ) : (
                            <div
                                className="flex h-full w-full items-center justify-center font-display text-2xl font-bold text-white"
                                style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}
                            >
                                {initials}
                            </div>
                        )}
                    </div>

                    {/* Name + meta */}
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Your swim coach")}</p>
                        <h1
                            className="mt-1 font-display text-2xl font-bold leading-tight text-[#0a1628] sm:text-3xl"
                            data-testid="coach-intro-name"
                        >
                            {coach.display_name}
                        </h1>
                        {(yearsLabel || locationLabel) && (
                            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#1a3a5c]/85">
                                {yearsLabel && (
                                    <span className="inline-flex items-center gap-1" data-testid="coach-intro-experience">
                                        <Award className="h-3.5 w-3.5" /> {yearsLabel}
                                    </span>
                                )}
                                {locationLabel && (
                                    <span className="inline-flex items-center gap-1" data-testid="coach-intro-location">
                                        <MapPin className="h-3.5 w-3.5" /> {locationLabel}
                                    </span>
                                )}
                            </div>
                        )}
                        {certs.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="coach-intro-certs">
                                {certs.slice(0, 4).map((c, i) => (
                                    <span
                                        // Phase 44.10 — use the cert string itself as the key
                                        // since cert names are unique-by-intent within a coach
                                        // profile. Fallback to index suffix only for the rare
                                        // case of duplicate cert strings.
                                        key={`cert-${c}-${i}`}
                                        className="rounded-full bg-[#0a1628]/5 px-2 py-0.5 text-[11px] font-medium text-[#0a1628]"
                                    >
                                        {c}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Bio — preserved line breaks so coaches who wrote
                    multi-paragraph descriptions display correctly. */}
                {bio && (
                    <p
                        className="mt-4 whitespace-pre-line text-[15px] leading-relaxed text-[#1a3a5c]"
                        data-testid="coach-intro-bio"
                    >
                        {bio}
                    </p>
                )}

                {/* Welcome message — the explicit "register here" instruction
                    the user asked for. Anchors the parent so they know what
                    to do next. Uses the coach's accent color for the
                    "Register with X" emphasis. */}
                <div
                    className="mt-5 rounded-xl p-4 text-center sm:text-left"
                    style={{ background: `${accent}14`, borderLeft: `4px solid ${accent}` }}
                    data-testid="coach-intro-welcome"
                >
                    <p className="text-sm leading-relaxed text-[#0a1628]">
                        <span className="font-semibold">{t42("Register with")} </span>
                        <strong className="font-display text-base font-bold text-[#0a1628]">{coach.display_name}</strong>
                        <span> {t42("by filling out the info below — it only takes a couple of minutes.")}</span>
                    </p>
                </div>
            </div>
        </div>
    );
}

/**
 * CoachIntroPill — compact version of the hero that lives in the
 * sticky header on every step AFTER the entry one. Keeps the coach's
 * face top-of-mind throughout the form without taking up the real
 * estate of the full hero.
 */
export function CoachIntroPill({ coach }) {
    const { t: t42 } = useLocaleR42();
    if (!coach || !coach.display_name) return null;
    const photoUrl = coach.photo_url || '';
    const initials = (coach.display_name || 'C')
        .split(/\s+/)
        .map((s) => s[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    const accent = coach.primary_color || '#00d4ff';
    return (
        <div className="flex items-center gap-2" data-testid="coach-intro-pill">
            <div
                className="h-8 w-8 shrink-0 overflow-hidden rounded-full ring-2 ring-white"
                style={{ boxShadow: `0 0 0 1.5px ${accent}55` }}
            >
                {photoUrl ? (
                    <img src={photoUrl} alt={coach.display_name} className="h-full w-full object-cover" />
                ) : (
                    <div
                        className="flex h-full w-full items-center justify-center text-[10px] font-bold text-white"
                        style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}
                    >
                        {initials}
                    </div>
                )}
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[#1a3a5c]/70 leading-tight">{t42("Registering with")}</div>
                <div className="truncate text-xs font-bold text-[#0a1628] leading-tight" data-testid="coach-intro-pill-name">
                    {coach.display_name}
                </div>
            </div>
        </div>
    );
}
